/**
 * Timeline axis with month labels, gridlines, and TODAY marker.
 */

interface TimelineGridProps {
  startDate: Date;
  endDate: Date;
}

function getMonthsBetween(start: Date, end: Date): { label: string; date: Date }[] {
  const months: { label: string; date: Date }[] = [];
  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  while (current <= end) {
    months.push({
      label: `${labels[current.getMonth()]} ${current.getFullYear()}`,
      date: new Date(current),
    });
    current.setMonth(current.getMonth() + 1);
  }
  return months;
}

export function dateToPercent(date: Date, rangeStart: Date, rangeEnd: Date): number {
  const total = rangeEnd.getTime() - rangeStart.getTime();
  if (total <= 0) return 0;
  const offset = date.getTime() - rangeStart.getTime();
  return Math.max(0, Math.min(100, (offset / total) * 100));
}

export function TimelineGrid({ startDate, endDate }: TimelineGridProps) {
  const months = getMonthsBetween(startDate, endDate);
  const today = new Date();
  const todayPct = dateToPercent(today, startDate, endDate);
  const showToday = todayPct > 0 && todayPct < 100;

  return (
    <div className="relative h-10 border-b border-slate-200 mb-2">
      {/* Month labels + gridlines */}
      {months.map((m) => {
        const pct = dateToPercent(m.date, startDate, endDate);
        return (
          <div
            key={m.label}
            className="absolute top-0 bottom-0"
            style={{ left: `${pct}%` }}
          >
            <div className="border-l border-slate-200 h-full" />
            <span className="absolute top-1 left-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">
              {m.label}
            </span>
          </div>
        );
      })}

      {/* TODAY marker */}
      {showToday && (
        <div
          className="absolute top-0 bottom-0 z-10"
          style={{ left: `${todayPct}%` }}
        >
          <div className="border-l-2 border-dashed border-brand-orange h-full opacity-60" />
          <span className="absolute -top-0.5 -translate-x-1/2 text-[9px] font-bold text-brand-orange bg-orange-50 px-1.5 py-0.5 rounded">
            TODAY
          </span>
        </div>
      )}
    </div>
  );
}
